import React, { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, ToggleButton, ToggleButtonGroup,
  Button, Typography, Stack, CircularProgress, FormHelperText, Divider, IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined";
import { toast } from "react-toastify";
import { downloadConversationMonitoringJson, getConversationMonitoringDates } from "../../../../services/apiService";

const MODES = { single: "single", comparison: "comparison" };

function formatRun(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Download JSON for Conversation Monitoring. Brand / Category / Persona are
 * chosen locally in this popup (seeded from the page's current selection, and
 * never written back to it); GET /dates is re-fetched for that selection.
 * Runs are addressed by id, so two runs on the same calendar day stay
 * distinguishable.
 *  - Single run:  run_id only.
 *  - Comparison:  run_id = recent run, compare_run_id = older baseline run.
 *
 * `options` is GET /options: [{ entity, categories: [{ category, personas[] }] }].
 */
export default function MonitoringJsonExportModal({
  open, onClose, customerId, options = [], initialEntity, initialCategory, initialPersona, defaultRunId,
}) {
  const [entity, setEntity] = useState("");
  const [category, setCategory] = useState("");
  const [persona, setPersona] = useState("");
  const [dates, setDates] = useState([]);
  const [datesLoading, setDatesLoading] = useState(false);
  const [mode, setMode] = useState(MODES.single);
  const [runId, setRunId] = useState("");
  const [baselineId, setBaselineId] = useState("");
  const [loading, setLoading] = useState(false);
  // Page's run only applies to the page's own selection, on first load after opening.
  const preferredRunRef = useRef(null);

  const entityOption = options.find((o) => o.entity === entity);
  const categoryOption = entityOption?.categories?.find((c) => c.category === category);
  const personaItems = categoryOption?.personas || [];
  const personaRequired = personaItems.length > 0;

  useEffect(() => {
    if (!open) return;
    setEntity(initialEntity || options[0]?.entity || "");
    setCategory(initialCategory || "");
    setPersona(initialPersona || "");
    setMode(MODES.single);
    setBaselineId("");
    preferredRunRef.current = defaultRunId || null;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEntityChange = (value) => {
    const next = options.find((o) => o.entity === value);
    const nextCategory = next?.categories?.[0];
    setEntity(value);
    setCategory(nextCategory?.category || "");
    setPersona(nextCategory?.personas?.[0] || "");
    preferredRunRef.current = null;
  };

  const handleCategoryChange = (value) => {
    const next = entityOption?.categories?.find((c) => c.category === value);
    setCategory(value);
    setPersona(next?.personas?.[0] || "");
    preferredRunRef.current = null;
  };

  const handlePersonaChange = (value) => {
    setPersona(value);
    preferredRunRef.current = null;
  };

  // Keep category / persona valid for the selected brand once options are known.
  useEffect(() => {
    if (!open || !entityOption) return;
    if (!entityOption.categories.some((c) => c.category === category)) {
      const first = entityOption.categories[0];
      setCategory(first?.category || "");
      setPersona(first?.personas?.[0] || "");
    } else if (personaItems.length && !personaItems.includes(persona)) {
      setPersona(personaItems[0]);
    }
  }, [open, entity, category, options]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !customerId || !entity || !category || (personaRequired && !persona)) {
      setDates([]);
      setRunId("");
      setBaselineId("");
      return undefined;
    }
    let cancelled = false;
    setDatesLoading(true);
    getConversationMonitoringDates({
      customerId,
      category,
      entity,
      persona: personaRequired ? persona : undefined,
    })
      .then((res) => {
        if (cancelled) return;
        const list = res?.data || [];
        setDates(list);
        const preferred = list.find((d) => d.id === preferredRunRef.current);
        setRunId(preferred?.id || list[0]?.id || "");
        setBaselineId("");
        setMode(MODES.single);
      })
      .catch(() => {
        if (cancelled) return;
        setDates([]);
        setRunId("");
      })
      .finally(() => {
        if (!cancelled) setDatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, customerId, entity, category, persona, personaRequired]);

  const recentRun = dates.find((d) => d.id === runId);
  // Baseline must be strictly older than the recent run.
  const baselineOptions = dates.filter(
    (d) => recentRun && new Date(d.execution_start_time) < new Date(recentRun.execution_start_time)
  );

  // Changing the recent run can invalidate a previously chosen baseline.
  useEffect(() => {
    if (baselineId && !baselineOptions.some((d) => d.id === baselineId)) setBaselineId("");
  }, [runId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isComparison = mode === MODES.comparison;
  // dates is latest-first, so a consecutive baseline is the entry right after the recent run.
  const recentIndex = dates.findIndex((d) => d.id === runId);
  const isConsecutive = isComparison && !!baselineId && recentIndex !== -1 && dates[recentIndex + 1]?.id === baselineId;
  const canDownload = !!runId && (!isComparison || !!baselineId) && !loading && !datesLoading;

  const handleDownload = async () => {
    setLoading(true);
    try {
      await downloadConversationMonitoringJson({
        customerId,
        category,
        entity,
        persona: personaRequired ? persona : undefined,
        runId,
        compareRunId: isComparison ? baselineId : undefined,
      });
      toast.success("The JSON file has been downloaded successfully.");
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to download JSON");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <Typography variant="h6" component="span">
          Reporting{entity ? ` — ${entity}` : ""}
        </Typography>
        <IconButton size="small" onClick={onClose} disabled={loading}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Brand</InputLabel>
            <Select value={entity} label="Brand" onChange={(e) => handleEntityChange(e.target.value)} disabled={loading}>
              {options.map((o) => (
                <MenuItem key={o.entity} value={o.entity}>{o.entity}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryOption ? category : ""}
              label="Category"
              onChange={(e) => handleCategoryChange(e.target.value)}
              disabled={loading || !entityOption}
            >
              {(entityOption?.categories || []).map((c) => (
                <MenuItem key={c.category} value={c.category}>{c.category}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {personaRequired && (
            <FormControl fullWidth size="small">
              <InputLabel>Persona</InputLabel>
              <Select
                value={personaItems.includes(persona) ? persona : ""}
                label="Persona"
                onChange={(e) => handlePersonaChange(e.target.value)}
                disabled={loading}
              >
                {personaItems.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Divider />

          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={mode}
            onChange={(_, value) => value && setMode(value)}
          >
            <ToggleButton value={MODES.single}>Single Run</ToggleButton>
            <ToggleButton value={MODES.comparison} disabled={dates.length < 2}>Compare Runs</ToggleButton>
          </ToggleButtonGroup>

          <Typography sx={{ fontSize: 13, color: "#667085" }}>
            {isComparison
              ? "Includes two runs — the recent run and an older baseline run — for comparison."
              : "Includes the full data of the selected run."}
          </Typography>

          <FormControl fullWidth size="small">
            <InputLabel>{isComparison ? "Recent Run" : "Run"}</InputLabel>
            <Select
              value={runId}
              label={isComparison ? "Recent Run" : "Run"}
              onChange={(e) => setRunId(e.target.value)}
              disabled={datesLoading || dates.length === 0}
            >
              {dates.map((d) => (
                <MenuItem key={d.id} value={d.id}>{formatRun(d.execution_start_time)}</MenuItem>
              ))}
            </Select>
            {!datesLoading && dates.length === 0 && (
              <FormHelperText>No completed runs for this selection.</FormHelperText>
            )}
          </FormControl>

          {isComparison && (
            <FormControl fullWidth size="small">
              <InputLabel>Baseline Run</InputLabel>
              <Select
                value={baselineId}
                label="Baseline Run"
                onChange={(e) => setBaselineId(e.target.value)}
                disabled={baselineOptions.length === 0}
              >
                {baselineOptions.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{formatRun(d.execution_start_time)}</MenuItem>
                ))}
              </Select>
              {baselineOptions.length === 0 && (
                <FormHelperText>No older run is available before the selected run.</FormHelperText>
              )}
              {isConsecutive && (
                <FormHelperText>
                  These are consecutive runs, so the trend explanation (what changed by 0.5 points or more) is included in the JSON.
                </FormHelperText>
              )}
            </FormControl>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={!canDownload}
          startIcon={loading ? null : <DataObjectOutlinedIcon />}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : "Download JSON"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
